import type { ComparisonTopic } from './types.js';

export interface FamiliarTerm {
  readonly product: 'سپیدار' | 'هلو';
  readonly term: string;
  readonly explanation: string;
}

/**
 * Verified against the vendors' own published teaching captions on 2026-11-02.
 * Familiar concepts only, not current menu paths or a feature-parity claim.
 * Sources are kept only in docs/product/contextual-page-help.md, never in the user guide.
 * Missing evidence stays absent. Sepidar's Dasht tutorials are not Sepidar evidence.
 */
export const HELP_COMPARISONS: Partial<Record<ComparisonTopic, readonly FamiliarTerm[]>> = {
  journal: [
    {
      product: 'سپیدار',
      term: 'صدور سند حسابداری',
      explanation:
        'نام آشنا برای ثبت یک رویداد مالی در حساب‌ها. در ساختار، پیش‌نویس و سند قطعی را از وضعیت سند تشخیص دهید.',
    },
    {
      product: 'هلو',
      term: 'صدور سند دستی',
      explanation:
        'اگر ثبت بدهکار و بستانکار را با این نام می‌شناسید، راهنمای سند حسابداری ساختار را دنبال کنید.',
    },
  ],
  accounts: [
    {
      product: 'سپیدار',
      term: 'کدینگ حسابداری',
      explanation:
        'ساختار دسته‌بندی حساب‌هاست. نام و کد حساب قبلی را پیش از استفاده با حساب متناظر در ساختار تطبیق دهید.',
    },
    {
      product: 'هلو',
      term: 'سرفصل حسابداری و کد حساب',
      explanation:
        'همان مفهوم دسته‌بندی ثبت‌های مالی را به یاد می‌آورد؛ کدها و سطح‌بندی دو نرم‌افزار لزوماً یکسان نیستند.',
    },
  ],
  parties: [
    {
      product: 'هلو',
      term: 'طرف حساب',
      explanation:
        'شخص یا شرکتی که با او معامله دارید. در ساختار، پروندهٔ طرف‌حساب با سند مالی و ماندهٔ بدهی او فرق دارد.',
    },
  ],
  products: [
    {
      product: 'سپیدار',
      term: 'تعریف کالا و خدمت',
      explanation:
        'برای معرفی چیزی است که می‌خرید یا می‌فروشید. تعریف کالا با ثبت موجودی یا خرید آن فرق دارد.',
    },
    {
      product: 'هلو',
      term: 'معرفی کالا',
      explanation:
        'نام آشنا برای ساخت پروندهٔ کالا؛ ورود و خروج کالا را از عملیات انبار ساختار دنبال کنید.',
    },
  ],
  warehouse: [
    {
      product: 'سپیدار',
      term: 'معرفی انبار',
      explanation:
        'برای مشخص‌کردن محل نگهداری کالا. ساخت یک انبار به‌تنهایی موجودی کالا ایجاد نمی‌کند.',
    },
    {
      product: 'هلو',
      term: 'انبار',
      explanation:
        'همان مفهوم محل نگهداری کالا را دارد؛ معرفی انبار با رسید، حواله یا انتقال کالا متفاوت است.',
    },
  ],
  inventory: [
    {
      product: 'هلو',
      term: 'کاردکس کالا و گزارش‌های انبار',
      explanation:
        'کاردکس برای دیدن گردش کالا آشناست. در ساختار هم موجودی فعلی، گردش کالا و شمارش انبار را متناسب با صفحهٔ خودشان بررسی کنید.',
    },
  ],
  invoice: [
    {
      product: 'سپیدار',
      term: 'فاکتور فروش',
      explanation:
        'ثبت فروش را با این نام می‌شناسید. در ساختار، سفارش، تحویل، فاکتور و دریافت وجه هرکدام وضعیت و اثر خود را دارند.',
    },
    {
      product: 'هلو',
      term: 'فاکتور فروش',
      explanation:
        'نام آشنا برای ثبت فروش؛ برای فهم مرحلهٔ فعلی، وضعیت فاکتور و اسناد مرتبط در ساختار را بخوانید.',
    },
  ],
  quote: [
    {
      product: 'هلو',
      term: 'پیش‌فاکتور',
      explanation:
        'پیشنهاد فروش پیش از فاکتور نهایی است. پیش‌فاکتور ساختار را با فروش قطعی یا دریافت پول یکی نگیرید.',
    },
  ],
  treasury: [
    {
      product: 'سپیدار',
      term: 'دریافت و پرداخت',
      explanation:
        'مفهوم آشنا برای پیگیری ورود و خروج پول. در ساختار، تراکنش، وضعیت آن و ارتباطش با اسناد را بررسی کنید.',
    },
    {
      product: 'هلو',
      term: 'امور مالی اتوماتیک',
      explanation:
        'ممکن است دریافت و پرداخت را از این بخش بشناسید. برای ثبت یا پیگیری در ساختار، مراحل همین صفحه را دنبال کنید.',
    },
  ],
  cheques: [
    {
      product: 'سپیدار',
      term: 'عملیات بانکی چک دریافتی و پرداختی',
      explanation:
        'نام آشنا برای پیگیری وضعیت چک‌هاست. در ساختار، نوع چک و اقدام مجاز در وضعیت فعلی را بررسی کنید.',
    },
    {
      product: 'هلو',
      term: 'چک پرداختی و چک خرجی',
      explanation:
        'این اصطلاحات در آموزش‌های هلو به کار می‌روند. برای وضعیت‌های دریافت، پرداخت یا واگذاری در ساختار، راهنمای همین صفحه را بخوانید.',
    },
  ],
  reports: [
    {
      product: 'سپیدار',
      term: 'گزارش گردش حساب و مانده حساب',
      explanation:
        'از خانوادهٔ گزارش‌های حسابداری است. گزارش‌های مختلف سؤال‌های متفاوتی را جواب می‌دهند؛ موضوع و فیلتر گزارش این صفحه را بخوانید.',
    },
    {
      product: 'هلو',
      term: 'گزارش‌های حسابداری و سود و زیان',
      explanation:
        'نام آشنا برای بررسی نتیجهٔ ثبت‌های مالی. نوع گزارش، دوره و محدودهٔ داده در ساختار را پیش از مقایسه کنترل کنید.',
    },
  ],
  opening: [
    {
      product: 'سپیدار',
      term: 'سند افتتاحیه',
      explanation:
        'برای شروع سال مالی آشناست. مراحل پایان سال و ایجاد اسناد مرتبط در ساختار را با همین راهنما دنبال کنید.',
    },
    {
      product: 'هلو',
      term: 'مانده طرف حساب‌ها در اول دوره',
      explanation: 'یکی از اطلاعات شروع دوره است؛ با کل فرایند بستن سال و سند افتتاحیه یکسان نیست.',
    },
  ],
};
